<template>
  <v-container class="SwitchChild switch-child py-5">
    <NotLinkedProfileAlert
      v-if="activeChild && !activeChild.parent_id"
      :id="activeChild.id"
      class="mx-3 mb-5 mx-sm-auto"
    />

    <div class="switch-child-title">
      <h1>Select child profile</h1>
      <div class="mb-8">Who wants to play now?</div>
      <SchSearch :input-value.sync="search" />
    </div>

    <v-tabs
      v-model="profilesFilterTab"
      show-arrows
    >
      <v-tab
        v-for="tab in profilesFilterTabs"
        :key="tab.value"
        :href="`#${tab.value}`"
      >
        {{ tab.title }}
      </v-tab>
    </v-tabs>

    <div
      v-if="filteredChildren.length"
      class="switch-child__profiles justify-center justify-md-start px-1 py-3"
    >
      <ProfileCard
        v-for="child in filteredChildren"
        :key="child.id"
        :profile="child"
        :loading="child.id === loadingProfileId"
        @click="selectAccount(child)"
        @remove="onProfileCardRemove"
      />

      <div class="pa-3">
        <v-card
          width="180"
          height="252"
          class="add-profile"
          @click="$emit('add-profile')"
        >
          <div class="add-profile__img">
            <img :src="avatarPlaceholderImageSrc" alt="">
            <span>Missing Your Profile?</span>
          </div>
          <v-toolbar
            bottom
            tile
            flat
            height="72"
            max-height="72"
            color="grey lighten-2"
          >
            <v-toolbar-title class="flex-grow-1 d-flex justify-center black--text">
              <v-icon class="mr-2" color="black">add</v-icon>
              <span>Add child</span>
            </v-toolbar-title>
          </v-toolbar>
          <span class="add-profile-title"></span>
        </v-card>
      </div>
    </div>
    <div
      v-else
      class="grey lighten-4 pa-4"
    >
      <NoProfilesBanner @add-profile="$emit('add-profile')" />
    </div>

    <LogOutProfileDialog
      v-model="logoutProfileDialog.show"
      :profile-id="logoutProfileDialog.profileId"
    />
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { AVATARS_FOLDER_URL } from '@/configs/urls';
import { findJWTByProfileId } from '@/helpers/auth';

const profilesFilterTabs = [
  { title: 'All', value: 'All-profiles' },
  { title: 'Authorized', value: 'Authorized-profiles' },
  { title: 'Parent account', value: 'Parent-account-profiles' },
  { title: 'Expired session', value: 'Expired-profiles' },
];

export default {
  name: 'SwitchChild',
  components: {
    NotLinkedProfileAlert: () => import('@/components/NotLinkedProfileAlert.vue'),
    SchSearch: () => import('@/components/form-inputs/SchSearch.vue'),
    ProfileCard: () => import('@/components/ProfileCard.vue'),
    NoProfilesBanner: () => import('@/components/profiles/NoProfilesBanner.vue'),
    LogOutProfileDialog: () => import('@/components/modals/LogOutProfileDialog.vue'),
  },
  data() {
    return {
      search: '',
      profilesFilterTabs,
      profilesFilterTab: profilesFilterTabs[0].value,
      loadingProfileId: null,
      logoutProfileDialog: {
        show: false,
        profileId: null,
      },
    };
  },
  computed: {
    ...mapGetters({
      parentAccount: 'parent/parentAccount',
      parentChildren: 'parent/parentChildren',
      activeChild: 'children/activeChildProfile',
      getChildrenBySearchStr: 'children/getChildrenBySearchStr',
    }),
    parentAccountData() {
      return this.parentAccount || {};
    },
    children() {
      return this.getChildrenBySearchStr(this.search);
    },
    filteredChildren() {
      switch (this.profilesFilterTab) {
        case profilesFilterTabs[0].value: return this.children;
        case profilesFilterTabs[1].value: return this.children.filter(this.hasValidTokenProfile);
        case profilesFilterTabs[2].value: return this.parentChildren;
        case profilesFilterTabs[3].value:
          return this.children.filter(c => !this.hasValidTokenProfile(c));
        default: return this.children;
      }
    },
    avatarPlaceholderImageSrc() {
      return `${AVATARS_FOLDER_URL}login-bg.png`;
    },
  },
  methods: {
    ...mapActions('children', [
      'switchChildProfile',
      'resetTimer',
    ]),
    async selectAccount(child) {
      if (this.loadingProfileId) return;

      this.loadingProfileId = child.id;

      await this.switchChildProfile({ profileId: child.id })
        .finally(() => (this.loadingProfileId = null));

      if (this.activeChild?.id) {
        if (this.$moment(this.activeChild.last_played).local().diff(this.$moment(), 'days') !== 0) {
          this.resetTimer(this.activeChild);
        }

        if (!this.activeChild.avatar) {
          this.$router.push({ name: 'newgame', query: { edit: 'avatar' } });
        } else if (!this.activeChild.nickname) {
          this.$router.push({ name: 'newgame', query: { edit: 'name' } });
        }
      }
    },
    onProfileCardRemove(child) {
      this.logoutProfileDialog.show = true;
      this.logoutProfileDialog.profileId = child.id;
    },
    hasValidTokenProfile(profile) {
      try {
        return !!findJWTByProfileId(this.$store.getters, profile.id);
      } catch {
        return false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.switch-child {
  &-title {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 56px;
  }

  &__profiles {
    display: flex;
    flex-wrap: wrap;

    .add-profile {
      display: flex;
      flex-direction: column;
      border: 1px solid #D8D8D8;
      color: #000;
      font-size: 32px;
      cursor: pointer;
      text-align: center;
      transition: 0.3s;

      &:hover {
        box-shadow: 0px 7px 8px -4px rgba(0, 0, 0, 0.2), 0px 12px 17px 2px rgba(0, 0, 0, 0.14),
        0px 5px 22px 4px rgba(0, 0, 0, 0.12);
      }

      &__img {
        flex-grow: 1;
        width: 100%;
        position: relative;
        background-color: #fff;
        display: flex;
        justify-content: center;
        align-items: center;

        span {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }
  }
}
</style>
